/**
 * Use the CSS tab above to style your Element's container.
 */
import {CardElement, useElements, useStripe} from '@stripe/react-stripe-js';
import {getAccountDetails} from 'actions/accountActions';
import Button from 'components/common/Button';
import ContainerHeader from 'components/common/ContainerHeader';
import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {updateCard, UpdateCardRequest} from 'services/stripeService';
import styled from 'styled-components';
import {colors, containerStyles} from 'styles/shared';
import {getLocalUser} from '../../../constants/selectors';
import toaster from '../../../helpers/uiHelper';
import './CardSectionStyles.css';

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      color: '#32325d',
      fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
      fontSmoothing: 'antialiased',
      fontSize: '16px',
      '::placeholder': {
        color: '#aab7c4'
      }
    },
    invalid: {
      color: '#fa755a',
      iconColor: '#fa755a'
    }
  }
};

const CardDetailsContainer = styled.div`
  width: 54.4rem;
  height: 24rem;
  padding: 4.4rem 4.9rem 4.9rem;
  margin-bottom: 5rem;
  ${containerStyles}
`;

const ButtonOverride = styled(Button)`
  border: 0.2rem solid ${colors.blue_main};
`;

function CardUpdateSection() {
  const dispatch = useDispatch();

  const [activePlan, setActivePlan] = useState('');
  const [paymentMethodId, setPaymentMethodId] = useState('');

  const user = useSelector(getLocalUser);
  const stripe = useStripe();
  const elements = useElements();

  useEffect(() => {
    selectPlan();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function selectPlan() {
    const userId = user?.id;

    if (!userId) return;

    let accountDetails: any = await dispatch(getAccountDetails(userId));

    if (!accountDetails) return;

    setActivePlan(accountDetails.plan.id);
  }

  const handleSubmit = async event => {
    event.preventDefault();
    if (!activePlan) {
      console.log('empty plan', activePlan);
      return;
    }

    if (!user) {
      console.log('user not authenticated');
      return;
    }

    if (!user.customer_id) {
      console.log('user not signed up');
      return;
    }

    if (!stripe || !elements) {
      console.log('stripe not ready');
      return;
    }

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      return;
    }

    const result = await stripe.createPaymentMethod({type: 'card', card: cardElement}).then(result => {
      console.log('PaymentMethod', result);
      if (result.error) {
        if (result.error.message) {
          toaster.showError(result.error.message);
        } else {
          toaster.showError('We were unable to register your payment method');
        }

        return;
      }
      if (result.paymentMethod === undefined) {
        toaster.showError('We were unable to register your payment method');
        return;
      }

      setPaymentMethodId(result.paymentMethod.id);

      return updateCard({
        customerId: user.customer_id,
        paymentMethodId: result.paymentMethod.id
      } as UpdateCardRequest);
    });
    if (result !== true) {
      toaster.showError('We were unable to register your payment method');
      return;
    }

    // Come cards support 3d secure authentication, but it seems this is only
    // available when stripe radar is enabled at an extra cost, so this has not been fully tested,
    // but will work when cards to not require auth or radar is disabled
    handlePaymentThatRequiresCustomerAction(result, paymentMethodId);
  };

  function handlePaymentThatRequiresCustomerAction(subscription, paymentMethodId) {
    if (!stripe || !elements) {
      console.log('stripe not ready');
      return;
    }

    let setupIntent = subscription.pending_setup_intent;

    if (setupIntent && setupIntent.status === 'requires_action') {
      return stripe
        .confirmCardSetup(setupIntent.client_secret, {
          payment_method: paymentMethodId
        })
        .then(result => {
          if (result.error) {
            console.log(result.error);
            toaster.showError('We could not authenticate your payment method');
            return false;
          } else {
            if (result.setupIntent?.status === 'succeeded') {
              // There's a risk of the customer closing the window before callback
              // execution. To handle this case, set up a webhook endpoint and
              // listen to setup_intent.succeeded.
              console.log('Payment method updated', result);
              toaster.showMessage('Payment method updated');
            }
          }
        });
    } else {
      // No customer action needed
      toaster.showMessage('Payment method updated');
    }
  }

  const isActive = stripe !== null && user.customer_id !== undefined && user.customer_id !== null;

  return (
    <CardDetailsContainer>
      <ContainerHeader>Card details</ContainerHeader>
      <CardElement options={CARD_ELEMENT_OPTIONS} />
      <ButtonOverride isActive={isActive} onClick={handleSubmit}>
        Update Payment Method
      </ButtonOverride>
    </CardDetailsContainer>
  );
}

export default CardUpdateSection;
